// The Farm Map's block read (FM01 UF-12 / UF-16), kept apart from FarmMap.tsx
// so "Prepare for offline" warms exactly what the map reads.
//
// The map used to read only DRAWN blocks — the read dropped any block with no
// boundary — so `/farm/map?block=<id>` for one opened nothing, Find a block
// could not list it, and the header's count was of drawn blocks only. It now
// reads every block the farm owns (useFarmOwnedBlocks: drained, not capped at
// a page) and the map draws the ones that have a shape.

import type { BlockBoundary } from '@/lib/blockBoundary'
import { blockContaining } from './scoutGps'
import { activePlantingsOn, plantingsInUseHa, type PlantingWindowFields } from '@/lib/plantings'

/** The select — part of useFarmOwnedBlocks' cache key, so the page and the
 *  offline prewarm task must use this one constant. */
export const FARM_MAP_BLOCK_COLUMNS =
  'block_id, block_name, variety, hectares, tree_count, is_bearing, notes, ' +
  'boundary, boundary_override, under_net, land_type, commodity_id, commodities:commodity_id ( name ), ' +
  'pucs:puc_id ( puc_code, grower_id, growers:grower_id ( name ) )'

export interface MapBlock {
  block_id:     string
  block_name:   string
  variety:      string | null
  hectares:     number | null
  tree_count:   number | null
  is_bearing:   boolean | null
  notes:        string | null
  /** The drawn shape (a packhouse override wins), or null: not drawn. */
  boundary:     BlockBoundary | null
  puc_code:     string | null
  grower_name:  string | null
  under_net:    boolean | null
  /** The block's RESOLVED crop (commodity_id → commodities.name); null = no
   *  crop set, or one typed that never resolved to the crop registry. */
  crop:         string | null
  /** ORCHARD (default) | PIVOT | FIELD | OTHER — a non-orchard block grows
   *  plantings, and has no tree count or bearing (FM01 UF-11 / R1). */
  land_type:    string
}

type Embed<T> = T | T[] | null | undefined
const one = <T>(v: Embed<T>): T | null => (Array.isArray(v) ? v[0] : v) ?? null

export interface MapBlockRow {
  block_id: string; block_name: string; variety: string | null; hectares: number | null
  tree_count: number | null; is_bearing: boolean | null; notes: string | null
  boundary: BlockBoundary | null; boundary_override: BlockBoundary | null; under_net: boolean | null
  commodity_id?: string | null; commodities?: Embed<{ name: string | null }>
  land_type?: string | null
  pucs?: Embed<{ puc_code: string | null; grower_id: string; growers?: Embed<{ name: string | null }> }>
}

export function toMapBlock(r: MapBlockRow): MapBlock {
  const puc = one(r.pucs)
  return {
    block_id:    r.block_id,
    block_name:  r.block_name,
    variety:     r.variety,
    hectares:    r.hectares,
    tree_count:  r.tree_count,
    is_bearing:  r.is_bearing,
    notes:       r.notes,
    boundary:    r.boundary_override ?? r.boundary ?? null,
    puc_code:    puc?.puc_code ?? null,
    grower_name: one(puc?.growers)?.name ?? null,
    under_net:   r.under_net ?? false,
    crop:        r.commodity_id ? (one(r.commodities)?.name?.trim() || null) : null,
    land_type:   r.land_type ?? 'ORCHARD',
  }
}

// ---------------------------------------------------------------------------
// FM01 UF-14: crop colours on a farm that grows more than one crop.
// ---------------------------------------------------------------------------

/** Distinct on the dark satellite and from each other; none of them is the
 *  overlays' red/amber/green signal colours' exact hex. */
const CROP_PALETTE = ['#38bdf8', '#f472b6', '#facc15', '#a78bfa', '#fb923c', '#2dd4bf', '#e879f9', '#a3e635']
export const NO_CROP_COLOUR = '#6b7280'
const NO_CROP_LABEL = 'No crop set'

export interface CropLegend {
  /** Two or more crops among the drawn blocks — a one-crop farm sees no change. */
  show:   boolean
  colour: (crop: string | null) => string
  /** Every drawn block counted once; "No crop set" last. */
  rows:   { label: string; colour: string; count: number }[]
}

export function cropLegend(drawn: readonly { crop: string | null }[]): CropLegend {
  const counts = new Map<string, number>()
  let none = 0
  for (const b of drawn) {
    if (b.crop) counts.set(b.crop, (counts.get(b.crop) ?? 0) + 1)
    else none++
  }
  const crops = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  const byCrop = new Map(crops.map(([c], i) => [c, CROP_PALETTE[i % CROP_PALETTE.length]]))
  const rows = crops.map(([label, count]) => ({ label, colour: byCrop.get(label)!, count }))
  if (none > 0) rows.push({ label: NO_CROP_LABEL, colour: NO_CROP_COLOUR, count: none })
  return { show: crops.length >= 2, colour: c => (c && byCrop.get(c)) || NO_CROP_COLOUR, rows }
}

/** The header line: "18 of 24 blocks drawn", or "6 blocks drawn" when every
 *  block is. */
export function drawnCountText(drawn: number, total: number): string {
  const noun = (n: number) => `block${n === 1 ? '' : 's'}`
  return drawn === total
    ? `${drawn} ${noun(drawn)} drawn`
    : `${drawn} of ${total} ${noun(total)} drawn`
}

/** FM01 UF-13: "The block I'm in" — the block one GPS fix falls inside, by
 *  the shared one-hit rule (blockContaining), or the sentence that says why
 *  there isn't one. */
export function blockImIn<B extends { block_id: string; boundary: BlockBoundary | null }>(
  fix: { lat: number; lng: number } | null, blocks: readonly B[],
): { block: B } | { note: string } {
  if (!fix) return { note: "Couldn't get your position — check that location is on for this app, then try again." }
  const hit = blockContaining(fix, blocks)
  if (hit.kind === 'one') {
    const block = blocks.find(b => b.block_id === hit.blockId)
    if (block) return { block }
  }
  return hit.kind === 'many'
    ? { note: "You're inside more than one drawn block — pick it from Find a block." }
    : { note: "You're not inside a drawn block." }
}

/** FM01 UF-15: the not-farmed hatch — `size`² RGBA pixels of diagonal amber
 *  stripes (two pixels wide), for map.addImage. */
export function hatchPixels(size = 8): { width: number; height: number; data: Uint8Array } {
  const data = new Uint8Array(size * size * 4)
  for (let i = 0; i < size * size; i++) {
    const x = i % size, y = Math.floor(i / size)
    if ((x + y) % size <= 1) data.set([251, 191, 36, 210], i * 4)
  }
  return { width: size, height: size, data }
}

// ---------------------------------------------------------------------------
// FM01 UF-11: a pivot or field block's panel shows what is in the ground.
// ---------------------------------------------------------------------------

export const isOrchard = (b: { land_type: string }) => b.land_type === 'ORCHARD'

type PanelPlanting = PlantingWindowFields & {
  project_code: string; sub_section?: string | null; size_ha: number | string | null
  crop: { name: string } | null
}

const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000)

/** "Section A · Carrots · 8.0 ha · day 38 of 90" for each planting live on
 *  `ymd` (the capture pickers' rule), newest cycle first. */
export function plantingLines(plantings: readonly PanelPlanting[], ymd: string): string[] {
  return activePlantingsOn([...plantings], ymd).map(p => {
    const day = daysBetween(p.cycle_start_date, ymd) + 1
    const span = p.expected_end_date ? daysBetween(p.cycle_start_date, p.expected_end_date) + 1 : null
    return [
      p.sub_section?.trim() || p.project_code,
      p.crop?.name ?? null,
      p.size_ha != null ? `${Number(p.size_ha).toFixed(1)} ha` : null,
      span ? `day ${day} of ${span}` : `day ${day}`,
    ].filter(Boolean).join(' · ')
  })
}

/** The Hectares tile on a pivot/field panel: "32 ha · 13.5 free" by the
 *  plantings panel's own in-use rule; just "32 ha" when the size is unknown. */
export function hectaresWithFree(hectares: number | null, plantings: readonly PanelPlanting[], ymd: string): string {
  if (hectares == null) return '—'
  const free = Math.max(0, hectares - plantingsInUseHa(plantings, ymd))
  return `${hectares} ha · ${free.toFixed(1)} free`
}
